import type { AppUserContext, PackageSummary, VisibleAction } from '@/types/logistics'
import type { PwaTone } from '@/components/pwa/tone'
import { UiDeliveryState } from '@/lib/status'
import { exceptionReasonsFor } from '@/lib/exception-workflow'

const ACTIONS: VisibleAction[] = [
  { id: 'accept_pickup', label: 'repairs.acceptPickup', endpoint: '/deliveries/{id}/accept-pickup', method: 'POST', permission: 'drops:create', primary: true },
  { id: 'accept_return', label: 'repairs.acceptReturn', endpoint: '/deliveries/{id}/accept-return', method: 'POST', permission: 'drops:create', primary: true },
  { id: 'release_return', label: 'repairs.releaseReturn', endpoint: '/deliveries/{id}/release-return', method: 'POST', permission: 'drops:create' },
  {
    id: 'confirm_sender_return',
    label: 'actions.confirm_sender_return',
    endpoint: '/deliveries/{id}/pod',
    method: 'POST',
    permission: 'drops:create',
    primary: true
  },
  {
    id: 'scan_reception',
    label: 'actions.scan_reception',
    endpoint: '/agent/packages/scan-reception',
    method: 'POST',
    permission: 'packages:receive',
    primary: true
  },
  {
    id: 'confirm_reception',
    label: 'actions.confirm_reception',
    endpoint: '/deliveries/{id}/receive',
    method: 'POST',
    permission: 'admin:exceptions',
    primary: true
  },
  {
    id: 'start_delivery',
    label: 'actions.start_delivery',
    endpoint: '/deliveries/{id}/out',
    method: 'POST',
    permission: 'packages:deliver',
    primary: true
  },
  {
    id: 'capture_pod',
    label: 'actions.capture_pod',
    endpoint: '/agent/packages/{id}/pod',
    method: 'POST',
    permission: 'packages:deliver',
    primary: true
  },
  {
    id: 'report_exception',
    label: 'actions.report_exception',
    endpoint: '/deliveries/{id}/exception',
    method: 'POST',
    permission: 'exceptions:create'
  },
  {
    id: 'request_return',
    label: 'actions.request_return',
    endpoint: '/agent/packages/{id}/return',
    method: 'POST',
    permission: 'packages:return'
  },
  {
    id: 'deposit_return',
    label: 'actions.deposit_return',
    endpoint: '/deliveries/{id}/deposit-return',
    method: 'POST',
    permission: 'admin:exceptions',
    primary: true
  },
  {
    id: 'mark_en_route',
    label: 'actions.mark_en_route',
    endpoint: '/deliveries/{id}/en-route',
    method: 'POST',
    permission: 'drops:create',
    primary: true
  },
  {
    id: 'scan_drop',
    label: 'actions.scan_drop',
    endpoint: '/courier/drops/{id}/scan',
    method: 'POST',
    permission: 'drops:create',
    primary: true
  },
  {
    id: 'capture_lot_photo',
    label: 'actions.capture_lot_photo',
    endpoint: '/courier/drops/{id}/lot-photo',
    method: 'POST',
    permission: 'drops:create'
  },
  {
    id: 'confirm_handover',
    label: 'actions.confirm_handover',
    endpoint: '/courier/drops/{id}/handover-code',
    method: 'POST',
    permission: 'drops:close'
  },
  {
    id: 'close_drop',
    label: 'actions.close_drop',
    endpoint: '/courier/drops/{id}/close',
    method: 'POST',
    permission: 'drops:close',
    primary: true
  },
  {
    id: 'confirm_customer_handover',
    label: 'actions.confirm_customer_handover',
    endpoint: '/agent/pickups/receive-from-customer',
    method: 'POST',
    permission: 'packages:receive',
    primary: true
  },
  {
    id: 'create_pickup_handover',
    label: 'actions.create_pickup_handover',
    endpoint: '/agent/pickup-sessions',
    method: 'POST',
    permission: 'drops:create',
    primary: true
  },
  {
    id: 'collect_pickup',
    label: 'actions.collect_pickup',
    endpoint: '/pickup-sessions/{id}/receive',
    method: 'POST',
    permission: 'drops:close',
    primary: true
  },
  {
    id: 'collect_return',
    label: 'actions.collect_return',
    endpoint: '/deliveries/{id}/collect-return',
    method: 'POST',
    permission: 'admin:exceptions',
    primary: true
  },
  {
    id: 'view_pod',
    label: 'actions.view_pod',
    endpoint: '/admin/packages/{id}/pod',
    method: 'GET',
    permission: 'pod:read'
  },
  {
    id: 'open_litigation',
    label: 'actions.open_litigation',
    endpoint: '/deliveries/{id}/open-dispute',
    method: 'POST',
    permission: 'admin:exceptions'
  },
  {
    id: 'retry_by_admin',
    label: 'actions.retry_by_admin',
    endpoint: '/deliveries/{id}/retry',
    method: 'POST',
    permission: 'admin:exceptions'
  },
  {
    id: 'return_to_sender',
    label: 'actions.return_to_sender',
    endpoint: '/deliveries/{id}/return-to-sender',
    method: 'POST',
    permission: 'admin:exceptions'
  },
  {
    id: 'resolve_as_delivered',
    label: 'actions.resolve_as_delivered',
    endpoint: '/deliveries/{id}/resolve-delivered',
    method: 'POST',
    permission: 'admin:exceptions'
  },
  {
    id: 'resolve_as_failed',
    label: 'actions.resolve_as_failed',
    endpoint: '/deliveries/{id}/resolve-failed',
    method: 'POST',
    permission: 'admin:exceptions'
  },
  {
    id: 'confirm_failed',
    label: 'actions.confirm_failed',
    endpoint: '/deliveries/{id}/confirm-failed',
    method: 'POST',
    permission: 'admin:exceptions'
  },
  {
    id: 'accept_damaged',
    label: 'actions.accept_damaged',
    endpoint: '/deliveries/{id}/accept-damaged',
    method: 'POST',
    permission: 'admin:exceptions'
  },
  {
    id: 'clear_agent',
    label: 'actions.clear_agent',
    endpoint: '/deliveries/{id}/clear-agent',
    method: 'POST',
    permission: 'admin:exceptions'
  },
  {
    id: 'confirm_loss_agent_fault',
    label: 'actions.confirm_loss_agent_fault',
    endpoint: '/deliveries/{id}/confirm-loss-agent-fault',
    method: 'POST',
    permission: 'admin:exceptions'
  },
  {
    id: 'cancel',
    label: 'actions.cancel',
    endpoint: '/deliveries/{id}/cancel',
    method: 'POST',
    permission: 'admin:exceptions'
  },
  {
    id: 'run_settlement',
    label: 'actions.run_settlement',
    endpoint: '/admin/settlements/run',
    method: 'POST',
    permission: 'admin:settlements',
    primary: true
  }
]

const stateActions: Record<PackageSummary['state'], VisibleAction['id'][]> = {
  [UiDeliveryState.Created]: ['mark_en_route', 'report_exception'],
  [UiDeliveryState.EnRoute]: ['scan_drop', 'report_exception'],
  [UiDeliveryState.Dropped]: ['confirm_reception', 'capture_lot_photo', 'confirm_handover', 'close_drop'],
  [UiDeliveryState.Received]: ['capture_pod', 'report_exception'],
  [UiDeliveryState.OutForDelivery]: ['capture_pod', 'report_exception'],
  [UiDeliveryState.Delivered]: ['view_pod'],
  [UiDeliveryState.Failed]: [],
  [UiDeliveryState.ReturnRequested]: ['deposit_return'],
  [UiDeliveryState.ReturnDeposited]: ['collect_return'],
  [UiDeliveryState.ReturnCollected]: ['view_pod'],
  [UiDeliveryState.Disputed]: ['view_pod'],
  [UiDeliveryState.Cancelled]: ['view_pod'],
  [UiDeliveryState.AwaitingPickup]: ['confirm_customer_handover'],
  /**
   * A parcel already rides the session its customer hand-over opened, so there is nothing
   * left for the agent to launch: the code is in their hand and the courier collects with it.
   */
  [UiDeliveryState.PickupReady]: []
}

const backendActionMap: Partial<Record<NonNullable<PackageSummary['availableActions']>[number], VisibleAction['id']>> = {
  drop: 'scan_drop',
  accept_pickup: 'accept_pickup',
  accept_return: 'accept_return',
  release_return: 'release_return',
  mark_en_route: 'mark_en_route',
  receive: 'confirm_reception',
  start_delivery: 'start_delivery',
  capture_pod: 'capture_pod',
  report_exception: 'report_exception',
  request_return: 'request_return',
  deposit_return: 'deposit_return',
  collect_return: 'collect_return',
  confirm_sender_return: 'confirm_sender_return',
  open_dispute: 'open_litigation',
  retry_by_admin: 'retry_by_admin',
  return_to_sender: 'return_to_sender',
  resolve_as_delivered: 'resolve_as_delivered',
  resolve_as_failed: 'resolve_as_failed',
  confirm_failed: 'confirm_failed',
  accept_damaged: 'accept_damaged',
  clear_agent: 'clear_agent',
  confirm_loss_agent_fault: 'confirm_loss_agent_fault',
  cancel: 'cancel',
  confirm_customer_handover: 'confirm_customer_handover',
  create_pickup_handover: 'create_pickup_handover',
  collect_pickup: 'collect_pickup'
}

// Actions reachable by role alone: the permission carried by the action belongs
// to another role's list, so the role check is what gates them.
const roleGatedActions: Partial<Record<VisibleAction['id'], AppUserContext['role']>> = {
  deposit_return: 'agent',
  collect_return: 'courier',
  accept_pickup: 'courier',
  accept_return: 'courier',
  release_return: 'courier',
  confirm_sender_return: 'courier',
  request_return: 'admin',
  create_pickup_handover: 'agent'
}

/**
 * The word for an action, for surfaces that must name an action they did not render themselves —
 * the offline queue's rows, which used to print `POST /deliveries/<uuid>/out` at an agent.
 *
 * Accepts either the backend's own action name (as `available_actions` reports it) or one of this
 * policy's ids, so a caller can pass whichever it happens to know, and resolves both through the
 * same list the action buttons come from. One vocabulary, so a queued row and the button that
 * queued it cannot disagree.
 */
export function actionLabelKeyFor(action: string | undefined): string | undefined {
  if (!action) {
    return undefined
  }

  const id = backendActionMap[action as keyof typeof backendActionMap] ?? action

  return ACTIONS.find((candidate) => candidate.id === id)?.label
}

/**
 * The picture and the lane colour for an action, used wherever that action is offered — the parcel
 * card in a list, and the bar docked at the bottom of its detail screen.
 *
 * They live here, next to the actions themselves, because the two surfaces had their own copies and
 * had drifted: "start delivering" was red on the list and green in the docked bar, which reads as
 * two different acts. An unmapped id falls back to a neutral arrow rather than throwing.
 */
const ACTION_EMOJI: Record<string, string> = {
  confirm_reception: '📦',
  capture_pod: '📸',
  start_delivery: '🚀',
  mark_en_route: '🚚',
  scan_drop: '📷',
  capture_lot_photo: '🖼️',
  collect_return: '📋',
  confirm_sender_return: '🤝',
  deposit_return: '↩️',
  report_exception: '⚠️',
  view_pod: '🖼️',
  confirm_customer_handover: '🤝',
  create_pickup_handover: '🔑'
}

/**
 * Three meanings, consistently: **red** go and do the thing, **green** close something out, **blue**
 * capture or move. Amber is deliberately absent — it is an ink for a wash, and a button filled with
 * it comes out brown.
 */
const ACTION_TONE: Record<string, PwaTone> = {
  confirm_reception: 'success',
  capture_pod: 'info',
  start_delivery: 'primary',
  report_exception: 'primary',
  deposit_return: 'primary',
  scan_drop: 'info',
  capture_lot_photo: 'info',
  collect_return: 'success',
  confirm_sender_return: 'success',
  mark_en_route: 'info',
  view_pod: 'info',
  confirm_customer_handover: 'primary',
  create_pickup_handover: 'primary'
}

export function actionEmoji(id: string): string {
  return ACTION_EMOJI[id] ?? '➡️'
}

export function actionTone(id: string): PwaTone {
  return ACTION_TONE[id] ?? 'neutral'
}

/**
 * An agent receives a parcel dropped for them. The action carries the admin permission because
 * the single-parcel API route is the console's, but the agent app does not use that route: it
 * confirms reception through the hand-over (the scan page, which the parcel's own page opens),
 * which checks the parcel is theirs. The backend only offers `receive` to that agent, so the
 * permission test here must not hide what the backend decided to offer.
 */
function isAgentReception(id: VisibleAction['id'], context: AppUserContext): boolean {
  return id === 'confirm_reception' && context.role === 'agent'
}

export function getVisibleActions(context: AppUserContext, item?: PackageSummary): VisibleAction[] {
  if (context.accountStatus !== 'active') {
    return []
  }

  const backendIds = item?.availableActions
    ?.map((action) => backendActionMap[action])
    .filter((action): action is VisibleAction['id'] => Boolean(action))
  const ids = item
    ? item.availableActions !== undefined
      ? backendIds ?? []
      : stateActions[item.state]
    : (['scan_reception', 'scan_drop', 'run_settlement'] as VisibleAction['id'][])

  return ACTIONS.filter((action) => ids.includes(action.id))
    .filter(action => action.id !== 'report_exception' || !item || (context.role !== 'agent' && context.role !== 'courier') || exceptionReasonsFor(context.role, item.state, item.allowedExceptionCodes).length > 0)
    .filter((action) => {
      const gatedRole = roleGatedActions[action.id]

      if (gatedRole) {
        return context.role === gatedRole
      }

      /**
       * `report_exception` is decided by the parcel, not by a table of states kept here.
       *
       * The backend publishes the actions a parcel currently allows *and* scopes that list to the
       * authenticated role, so a second, hardcoded per-role state table in the client can only ever
       * contradict it — and it did: an agent was refused the exception button on parcels in
       * `received` and `en_route` for which the backend had offered exactly that action. The button
       * therefore appeared or vanished according to which of the two sources you trusted, so it
       * looked like a timing problem rather than the disagreement it was.
       *
       * Above, when the backend sends no action list, `stateActions` is the fallback and already
       * restricts this action to the states that support it.
       */
      return true
    })
    .filter((action) => Boolean(roleGatedActions[action.id]) || context.permissions.includes(action.permission) || isAgentReception(action.id, context))
    .map((action) => ({
      ...action,
      endpoint: item ? action.endpoint.replace('{id}', item.deliveryId ?? item.id) : action.endpoint,
      disabledReason: undefined
    }))
}

/**
 * Money, turned into text in the one way the whole project agrees on.
 *
 * Amounts reach the view as whole dirhams (the API's `*_centimes` fields are divided by 100 on the way
 * in) and MAD is conventionally written without decimals — "150 MAD". That only reads correctly while
 * the amount IS whole: commission splits and percentage fees can produce centimes, and rounding those
 * away prints a number that is not the amount. So the precision follows the value: whole amounts keep
 * the compact form, anything with centimes shows them.
 *
 * This is the ONLY place money becomes text. `formatMoneyDecimals` was a second, always-2-decimals
 * implementation and the two were mixed across screens — the same COD read "150 MAD" on one page and
 * "150,00 MAD" on another, twice on the same page — so it is now an alias and cannot drift again.
 * Use `formatMoney` in new code.
 */
export function formatMoney(amount: number, currency: string) {
  const value = Number.isFinite(amount) ? amount : 0
  const whole = Math.abs(value % 1) < 1e-9 || Math.abs((value % 1) - 1) < 1e-9

  const formattedAmount = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2
  }).format(value)

  return `${formattedAmount} ${currency}`
}

/** Kept as an alias so the call sites that already use it cannot render money differently. */
export function formatMoneyDecimals(amount: number, currency: string) {
  return formatMoney(amount, currency)
}

/**
 * The same money when what you hold is the API's raw centimes field
 * (`packages.cod_amount_centimes`, `pod_records.cash_collected_centimes`, …).
 *
 * Prefer the view model's dirham amount; this exists so a raw API field shown straight from the
 * response is divided by 100 in ONE place instead of a local copy per page. Returns null for a
 * missing value, so the caller decides how to word the absence.
 */
export function formatCentimes(centimes: number | null | undefined, currency = 'MAD'): string | null {
  if (centimes === null || centimes === undefined) return null

  // The one place in the frontend where a raw API centime field is divided by 100.
  return formatMoney(Number(centimes) / 100, currency)
}

/**
 * Whatever a money field currently holds, as the reader typed it.
 *
 * A `type="number"` input hands its value to Vue as a *number*, not a string, so a
 * `ref<string>` behind one really holds a number from the first keystroke on. Calling
 * `.trim()` on it — the obvious way to ask "did they type anything?" — throws a TypeError
 * and kills the submit handler. Every check on an amount goes through here instead.
 */
export function amountText(value: unknown): string {
  return String(value ?? '').trim()
}

/** The same value as a number, or `undefined` when it is empty or not a number. */
export function amountNumber(value: unknown): number | undefined {
  const text = amountText(value).replace(',', '.')

  if (text === '') return undefined

  const parsed = Number(text)
  return Number.isFinite(parsed) ? parsed : undefined
}

export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

/**
 * An idempotency key is a random UUID — nothing is derived from `prefix`. It is accepted anyway
 * because call sites pass one to read as what the key is FOR, which is worth keeping. The leading
 * underscore says "intentionally unused", but this project's lint config does not honour that
 * convention, so the intent has to be stated twice.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function createIdempotencyKey(_prefix: string) {
  return generateUUID()
}
